/**
 * hum-detect.js — is there MAINS HUM in this recording, and at 50 or 60 Hz?
 *
 * Mains hum is a physical thing with a physical signature: narrow lines at
 * exactly k·f0, f0 within a few tenths of a hertz of 50 or 60, present the
 * WHOLE time. Everything else that lives at 50–300 Hz — a bass line, a kick
 * drum, the pitch of a voice, a fridge rumble — is either not narrow, not on
 * the grid, or not there all the time. So the detector asks exactly that:
 *
 *   1. per ~2-s window, a power spectrum (0.49 Hz resolution after decimating
 *      to ~4 kHz — the lines of interest are below 800 Hz);
 *   2. per bin, the 25th percentile ACROSS windows — what survives is what is
 *      present in ≥ 3/4 of the windows (hum, steady noise); speech and notes
 *      come and go and fall away;
 *   3. at k·50 and k·60 (k = 1…12): a line counts when it stands ≥ 8 dB over
 *      its neighbourhood (2–8 Hz away) and sits within 0.3 Hz·k of the grid;
 *      the lines of one mains share one f0 (anchored on the strongest of
 *      k ≤ 3), so a note that happens to fall near 350 Hz is not a harmonic;
 *   4. the same search at six NON-mains fundamentals is the file's chance
 *      level (a sustained organ chord makes lines anywhere).
 *
 * Why this exists (2026-09-24, comment #1740 under /denoise): the old test was
 * the share of spectral magnitude within ±32 Hz of 50/60/100/…/300 Hz in ONE
 * 46-ms FFT at the end of the first 10 s — i.e. «how much of the sound is below
 * 330 Hz». It called 2/3 of all uploads «hum» (RU 30 d: 3595 of 5372), among
 * them 14 of 21 clean speech clips and 29 of 50 clean songs of a labelled
 * corpus, and only ever notched the 50 Hz family — useless in 60 Hz countries.
 * This detector on the same corpus: 0 of 21 speech and 0 of 23 songs without
 * hum flagged; real 60 Hz hum found in recordings that were labelled clean.
 *
 *   HumDetect.analyze(audioBufferOrChannel, sampleRate?, opts?) → {
 *     family:  50 | 60 | null,     // the mains family, null = no hum found
 *     f0:      number | null,      // estimated fundamental (Hz, ±0.05)
 *     lines:   [{ k, prom }],      // harmonics found (prom = dB over neighbours)
 *     harmonics: number,           // highest k found (0 = none)
 *     humRel:  number | null,      // hum power relative to the programme, dB
 *     strongest: number,           // prominence of the strongest line, dB
 *     chance:  number,             // lines found at non-mains fundamentals
 *     windows: number,             // spectra averaged (≥ 3 needed)
 *     content: 'speech' | 'music' | null
 *   }
 *
 * The verdict (`family`) needs two harmonics of one f0, or ONE strong line
 * (≥ 12 dB) at k ≤ 2 — a transformer hum is mostly its fundamental. On MUSIC
 * (opts.content === 'music') a single line is not enough: generated songs carry
 * a steady 50.00 Hz line from their codec's frame rate at −45…−70 dB — real,
 * inaudible, not mains — so music needs two harmonics AND humRel ≥ −40 dB.
 * Pure function: no DOM, no telemetry.
 */
(function () {
    'use strict';

    var TARGET_SR = 4000;     // decimate to about this rate
    var WIN_SEC = 1.4;        // window ≥ this long → ≤ 0.5 Hz bins at 4 kHz
    var K = 12;               // harmonics examined
    var T = 8;                // dB over the neighbourhood for a line to count
    var STRONG = 12;          // a lone line this prominent at k ≤ 2 is hum
    var TOL0 = 0.3;           // |f/k − F| tolerance, Hz
    var Q = 0.25;             // percentile across windows
    var MAX_SEC = 12;         // analysed length (the page decodes 12 s bounded)
    var CONTROLS = [44.2, 46.7, 53.3, 56.1, 64.4, 67.3];
    // Harmonics of ONE mains: |f_k − k·f0| ≤ 0.08 + 0.03·k Hz (interpolation
    // error + grid drift over 12 s). Wider let a sung F4 (349.2 Hz) pass as the
    // 7th harmonic of 49.95 Hz on two generated songs.
    var COHERE0 = 0.08, COHERE_K = 0.03;
    // A lone line must also be LOUD enough to matter: generated songs carry a
    // steady 50.00 Hz codec line 47–60 dB under the programme (inaudible),
    // and a song the content classifier takes for speech would read it as hum.
    var LONE_MIN_REL = -50;

    function monoOf(input, sr, maxSec) {
        var ch;
        if (input && typeof input.getChannelData === 'function') {
            sr = input.sampleRate;
            var n = Math.min(input.length, Math.floor(sr * maxSec));
            ch = new Float32Array(n);
            var chans = input.numberOfChannels || 1;
            for (var c = 0; c < chans; c++) {
                var d = input.getChannelData(c);
                for (var i = 0; i < n; i++) ch[i] += d[i] / chans;
            }
        } else {
            ch = input.length > sr * maxSec ? input.subarray(0, Math.floor(sr * maxSec)) : input;
        }
        return { x: ch, sr: sr };
    }

    // Blackman-windowed sinc lowpass at 1.5 kHz, evaluated only at the kept
    // samples. Aliases landing below 800 Hz come from ≥ 3.2 kHz: stopband.
    function decimate(x, sr) {
        var D = Math.max(1, Math.floor(sr / TARGET_SR));
        if (D === 1) return { y: x, sr: sr };
        var L = 8 * D + 1, h = new Float64Array(L), fc = 1500 / sr, sum = 0, M = L - 1;
        for (var i = 0; i < L; i++) {
            var m = i - M / 2;
            var sinc = m === 0 ? 2 * fc : Math.sin(2 * Math.PI * fc * m) / (Math.PI * m);
            var w = 0.42 - 0.5 * Math.cos(2 * Math.PI * i / M) + 0.08 * Math.cos(4 * Math.PI * i / M);
            h[i] = sinc * w; sum += h[i];
        }
        for (var j = 0; j < L; j++) h[j] /= sum;
        var n = Math.floor((x.length - L) / D) + 1;
        if (n < 1) return { y: new Float32Array(0), sr: sr / D };
        var y = new Float32Array(n);
        for (var o = 0; o < n; o++) {
            var s = 0, base = o * D;
            for (var t = 0; t < L; t++) s += x[base + t] * h[t];
            y[o] = s;
        }
        return { y: y, sr: sr / D };
    }

    function fft(re, im) {
        var n = re.length;
        for (var i = 1, j = 0; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) { var t = re[i]; re[i] = re[j]; re[j] = t; t = im[i]; im[i] = im[j]; im[j] = t; }
        }
        for (var len = 2; len <= n; len <<= 1) {
            var ang = -2 * Math.PI / len, wr = Math.cos(ang), wi = Math.sin(ang), half = len >> 1;
            for (var a0 = 0; a0 < n; a0 += len) {
                var cr = 1, ci = 0;
                for (var k = 0; k < half; k++) {
                    var a = a0 + k, b = a + half;
                    var xr = re[b] * cr - im[b] * ci, xi = re[b] * ci + im[b] * cr;
                    re[b] = re[a] - xr; im[b] = im[a] - xi; re[a] += xr; im[a] += xi;
                    var tt = cr * wr - ci * wi; ci = cr * wi + ci * wr; cr = tt;
                }
            }
        }
    }

    // Per-bin q-th percentile, across windows, of the windowed PSD
    // (mean-square per bin: a line's 3 central bins sum to its mean square).
    function percentileSpectrum(y, sr, fMax) {
        var N = 1; while (N < sr * WIN_SEC) N <<= 1;
        var hop = N >> 1, binHz = sr / N, B = Math.min(N / 2, Math.ceil(fMax / binHz) + 20);
        var win = new Float64Array(N), S2 = 0;
        for (var i = 0; i < N; i++) { win[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / N); S2 += win[i] * win[i]; }
        var rows = [], re = new Float64Array(N), im = new Float64Array(N);
        for (var s = 0; s + N <= y.length; s += hop) {
            for (var j = 0; j < N; j++) { re[j] = y[s + j] * win[j]; im[j] = 0; }
            fft(re, im);
            var P = new Float64Array(B);
            for (var b = 0; b < B; b++) P[b] = 2 * (re[b] * re[b] + im[b] * im[b]) / (S2 * N);
            rows.push(P);
        }
        var W = rows.length, out = new Float64Array(B), col = new Float64Array(W);
        if (!W) return { P: out, binHz: binHz, W: 0 };
        var qi = Math.min(W - 1, Math.max(0, Math.round(Q * (W - 1))));
        for (var bb = 0; bb < B; bb++) {
            for (var w = 0; w < W; w++) col[w] = rows[w][bb];
            col.sort();
            out[bb] = col[qi];
        }
        return { P: out, binHz: binHz, W: W };
    }

    function median(arr) {
        var s = Float64Array.from(arr).sort(), n = s.length;
        return n ? (n & 1 ? s[n >> 1] : 0.5 * (s[n / 2 - 1] + s[n / 2])) : 0;
    }

    // The strongest bin within ±tol of fk: prominence over the median of a
    // ring 4…16 bins away, interpolated frequency, excess power.
    function lineAt(P, binHz, fk, tolHz) {
        var lo = Math.max(2, Math.floor((fk - tolHz) / binHz)), hi = Math.min(P.length - 3, Math.ceil((fk + tolHz) / binHz));
        var pk = lo;
        for (var b = lo; b <= hi; b++) if (P[b] > P[pk]) pk = b;
        var db = function (i) { return 10 * Math.log10(P[i] + 1e-30); };
        var a = db(pk - 1), c0 = db(pk), c = db(pk + 1), den = a - 2 * c0 + c;
        var d = den < 0 ? 0.5 * (a - c) / den : 0;
        var f = (pk + Math.max(-0.5, Math.min(0.5, d))) * binHz;
        var b0 = Math.round(fk / binHz), ring = [];
        for (var r = 4; r <= 16; r++) {
            if (b0 - r >= 1) ring.push(P[b0 - r]);
            if (b0 + r < P.length) ring.push(P[b0 + r]);
        }
        var floor = Math.max(median(ring), 1e-13);
        return {
            f: f,
            prom: Math.min(60, c0 - 10 * Math.log10(floor)),
            excess: Math.max(0, P[pk - 1] + P[pk] + P[pk + 1] - 3 * floor)
        };
    }

    function family(P, binHz, F) {
        var found = [];
        for (var k = 1; k <= K; k++) {
            var fk = k * F;
            if (fk / binHz + 18 >= P.length) break;
            var L = lineAt(P, binHz, fk, TOL0 * k + binHz);
            if (L.prom >= T && Math.abs(L.f / k - F) <= TOL0) found.push({ k: k, f0: L.f / k, prom: L.prom, excess: L.excess });
        }
        var low = found.filter(function (l) { return l.k <= 3; }).sort(function (a, b) { return b.prom - a.prom; });
        if (!low.length) return { n: 0, f0: null, lines: [] };
        var f0 = low[0].f0;
        var lines = found.filter(function (l) { return Math.abs(l.f0 - f0) * l.k <= COHERE0 + COHERE_K * l.k; });
        return { n: lines.length, f0: f0, lines: lines };
    }

    function analyze(input, sampleRate, opts) {
        opts = opts || {};
        var m = monoOf(input, sampleRate, opts.maxSec || MAX_SEC);
        var d = decimate(m.x, m.sr);
        var sp = percentileSpectrum(d.y, d.sr, 60 * K + 20);
        var content = opts.content === 'music' || opts.content === 'speech' ? opts.content : null;
        var none = { family: null, f0: null, lines: [], harmonics: 0, humRel: null, strongest: 0, chance: 0, windows: sp.W, content: content };
        if (sp.W < 3) return none;

        var ms = 0;
        for (var i = 0; i < m.x.length; i++) ms += m.x[i] * m.x[i];
        ms /= Math.max(1, m.x.length);
        if (!(ms > 1e-12)) return none;

        var f50 = family(sp.P, sp.binHz, 50), f60 = family(sp.P, sp.binHz, 60);
        var chance = 0;
        for (var c = 0; c < CONTROLS.length; c++) chance = Math.max(chance, family(sp.P, sp.binHz, CONTROLS[c]).n);
        var strongestOf = function (fam) { return fam.lines.reduce(function (s, l) { return Math.max(s, l.prom); }, 0); };
        var top = (f50.n > f60.n || (f50.n === f60.n && strongestOf(f50) >= strongestOf(f60))) ? f50 : f60;
        if (!top.n) return none;

        var power = top.lines.reduce(function (s, l) { return s + l.excess; }, 0);
        var humRel = power > 0 ? 10 * Math.log10(power / ms) : null;
        var strongest = strongestOf(top);
        var lowStrong = top.lines.some(function (l) { return l.k <= 2 && l.prom >= STRONG; }) && humRel !== null && humRel >= LONE_MIN_REL;
        var hum = content === 'music'
            ? (top.n >= 2 && humRel !== null && humRel >= -40)
            : (top.n >= 2 || lowStrong);
        var res = {
            family: hum ? (top === f50 ? 50 : 60) : null,
            f0: hum ? Math.round(top.f0 * 100) / 100 : null,
            lines: top.lines.map(function (l) { return { k: l.k, prom: Math.round(l.prom) }; }),
            harmonics: top.lines.reduce(function (s, l) { return Math.max(s, l.k); }, 0),
            humRel: humRel === null ? null : Math.round(humRel * 10) / 10,
            strongest: Math.round(strongest),
            chance: chance,
            windows: sp.W,
            content: content
        };
        return res;
    }

    window.HumDetect = { analyze: analyze };
})();
